import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  applyNodeChanges,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { DeviceNode } from './nodes/DeviceNode';
import { SheetBackground } from './SheetBackground';
import { ElementZoomControl } from './ElementZoomControl';
import { ConnectionEdge } from './edges/ConnectionEdge';
import { useStore, getActivePage } from '@/state/store';
import { SHEET } from '@/lib/svgExport';
import { computeParallelLanes, computeSideBundles } from '@/lib/connections';
import { buildNodeResolver, displayEdgesForPage } from '@/lib/detail';
import { useI18n } from '@/i18n/I18nContext';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PanelBottom, PanelLeft, Grid3x3, Filter, Tag } from 'lucide-react';

const nodeTypes = { device: DeviceNode };
const edgeTypes = { connection: ConnectionEdge };

export const DiagramCanvas = () => {
  const { t } = useI18n();
  const page = useStore(getActivePage);
  const doc = useStore((s) => s.doc);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const setPageGraph = useStore((s) => s.setPageGraph);
  const setPageLabelPos = useStore((s) => s.setPageLabelPos);
  const addEdgeConn = useStore((s) => s.addEdgeConn);
  const addDevice = useStore((s) => s.addDevice);
  const selectNode = useStore((s) => s.selectNode);
  const selectEdge = useStore((s) => s.selectEdge);
  const selectedEdgeId = useStore((s) => s.selectedEdgeId);
  const deleteNode = useStore((s) => s.deleteNode);
  const deleteEdge = useStore((s) => s.deleteEdge);
  const snapEnabled = useStore((s) => s.snapEnabled);
  const setSnapEnabled = useStore((s) => s.setSnapEnabled);
  const showPortLabels = useStore((s) => s.showPortLabels);
  const setShowPortLabels = useStore((s) => s.setShowPortLabels);
  const vlans = useStore((s) => s.doc.vlans);
  const highlightVlanRef = useStore((s) => s.highlightVlanRef);
  const setHighlightVlanRef = useStore((s) => s.setHighlightVlanRef);
  const { screenToFlowPosition, fitBounds } = useReactFlow();
  const wrapperRef = useRef(null);

  // Show the WHOLE sheet (A3 page incl. CAD frame) on page switch / mount,
  // instead of fitting to the device nodes.
  const fitSheet = useCallback(() => {
    const s = page?.contentScale || 1;
    fitBounds({ x: 0, y: 0, width: SHEET.w / s, height: SHEET.h / s }, { padding: 0.04, duration: 200 });
  }, [fitBounds, page?.contentScale]);

  useEffect(() => {
    if (!page?.id) return undefined;
    const raf = requestAnimationFrame(fitSheet);
    return () => cancelAnimationFrame(raf);
  }, [page?.id, fitSheet]);

  // Resolve alias placements to their master device node so a clone renders
  // with the master's icon/label/VLAN/IP/ports (single source of truth).
  const resolveMaster = useMemo(() => buildNodeResolver(doc), [doc]);

  const rfNodes = useMemo(
    () =>
      (page?.nodes || []).map((n) => {
        if (n.data?.aliasOf) {
          const master = resolveMaster(n.id);
          return {
            id: n.id,
            type: 'device',
            position: n.position,
            selected: n.id === selectedNodeId,
            data: { ...(master?.data || {}), aliasOf: n.data.aliasOf },
          };
        }
        return { ...n, type: 'device', selected: n.id === selectedNodeId };
      }),
    [page, selectedNodeId, resolveMaster]
  );

  const rfEdges = useMemo(() => {
    const portName = (nodeId, portId) => {
      if (!portId) return '';
      const master = resolveMaster(nodeId);
      const pts = master?.data?.ports || [];
      return pts.find((p) => p.id === portId)?.name || '';
    };
    // Connections form ONE global topology: an edge is shown on every page where
    // both endpoint devices are present (as master or clone). Edges whose home
    // page differs are flagged `mirrored` and drawn subtly (see ConnectionEdge).
    const displayEdges = displayEdgesForPage(doc, page);
    // Fan out only connections that share the SAME endpoints (device + side/
    // handle) — see computeParallelLanes. Cables on different sides don't
    // overlap, so they must not share a lane budget (that previously shifted
    // visible lines, hid some, and left uneven gaps).
    const lanes = computeParallelLanes(displayEdges);
    // Bundle busy device sides: >3 cables on one (device, side) converge on a
    // single anchor with a count label instead of many fanned lines.
    const bundles = computeSideBundles(displayEdges);
    const lagById = {};
    (doc.pages || []).forEach((p) => (p.lags || []).forEach((l) => (lagById[l.id] = l)));
    return displayEdges.map((e) => {
      const lane = lanes[e.id] || { index: 0, count: 1 };
      const bundle = bundles[e.id] || {};
      const lag = e.lagId ? lagById[e.lagId] : null;
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceSide || undefined,
        targetHandle: e.targetSide || undefined,
        type: 'connection',
        selected: e.id === selectedEdgeId,
        data: {
          connType: e.connType || 'ethernet',
          mirrored: !!e.__mirrored,
          sourcePort: portName(e.source, e.sourcePortId),
          targetPort: portName(e.target, e.targetPortId),
          parallelIndex: lane.index,
          parallelCount: lane.count,
          bundleSourceCollapsed: !!bundle.sourceCollapsed,
          bundleTargetCollapsed: !!bundle.targetCollapsed,
          bundleSourceCount: bundle.sourceCount || 0,
          bundleTargetCount: bundle.targetCount || 0,
          bundleSourceLabelOwner: !!bundle.sourceLabelOwner,
          bundleTargetLabelOwner: !!bundle.targetLabelOwner,
          lagName: lag ? lag.name : null,
          lagColor: lag ? lag.color : null,
        },
      };
    });
  }, [doc, page, selectedEdgeId, resolveMaster]);

  const onNodesChange = useCallback(
    (changes) => {
      const meaningful = changes.filter((c) => c.type === 'position');
      if (!meaningful.length) return;
      const updated = applyNodeChanges(meaningful, rfNodes);
      const posById = {};
      updated.forEach((n) => {
        posById[n.id] = n.position;
      });
      // Persist the ORIGINAL stored nodes with only their position updated. This
      // keeps alias nodes normalised ({ aliasOf } only) rather than writing the
      // merged master display data back into them.
      const devs = (page?.nodes || []).map((orig) => ({ ...orig, position: posById[orig.id] || orig.position }));
      if (page) setPageGraph(page.id, devs, undefined);
    },
    [rfNodes, page, setPageGraph]
  );

  const onConnect = useCallback(
    (conn) => {
      if (!conn.source || !conn.target || conn.source === conn.target) return;
      const sides = ['l', 'r', 't', 'b'];
      addEdgeConn({
        source: conn.source,
        target: conn.target,
        sourceSide: sides.includes(conn.sourceHandle) ? conn.sourceHandle : undefined,
        targetSide: sides.includes(conn.targetHandle) ? conn.targetHandle : undefined,
      });
    },
    [addEdgeConn]
  );

  const onNodesDelete = useCallback((nodes) => nodes.forEach((n) => deleteNode(n.id)), [deleteNode]);
  const onEdgesDelete = useCallback((edges) => edges.forEach((e) => deleteEdge(e.id)), [deleteEdge]);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const deviceType = event.dataTransfer.getData('application/netdraw-device');
      if (!deviceType) return;
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      addDevice(deviceType, { x: Math.round(position.x - 32), y: Math.round(position.y - 28) });
    },
    [screenToFlowPosition, addDevice]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const labelPos = page?.labelPos === 'left' ? 'left' : 'below';

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-[#FBFBFA] px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('label_position')}</span>
        <ToggleGroup
          type="single"
          value={labelPos}
          onValueChange={(v) => v && page && setPageLabelPos(page.id, v)}
          className="gap-1"
          data-testid="label-pos-toggle"
        >
          <ToggleGroupItem value="below" className="h-7 gap-1.5 px-2.5 text-xs" data-testid="label-pos-below">
            <PanelBottom className="h-3.5 w-3.5" /> {t('label_below')}
          </ToggleGroupItem>
          <ToggleGroupItem value="left" className="h-7 gap-1.5 px-2.5 text-xs" data-testid="label-pos-left">
            <PanelLeft className="h-3.5 w-3.5" /> {t('label_left')}
          </ToggleGroupItem>
        </ToggleGroup>

        <div className="mx-1 h-5 w-px bg-border" />

        <div className="flex items-center gap-2">
          <Grid3x3 className="h-3.5 w-3.5 text-muted-foreground" />
          <Switch
            id="snap-grid"
            checked={snapEnabled}
            onCheckedChange={setSnapEnabled}
            data-testid="snap-grid-switch"
          />
          <Label htmlFor="snap-grid" className="cursor-pointer text-xs text-[#2A3138]" title={snapEnabled ? t('snap_grid_on') : t('snap_grid_off')}>
            {t('snap_grid')}
          </Label>
        </div>

        <div className="mx-1 h-5 w-px bg-border" />

        <div className="flex items-center gap-2">
          <Tag className="h-3.5 w-3.5 text-muted-foreground" />
          <Switch
            id="show-port-labels"
            checked={showPortLabels}
            onCheckedChange={setShowPortLabels}
            data-testid="show-port-labels-switch"
          />
          <Label htmlFor="show-port-labels" className="cursor-pointer text-xs text-[#2A3138]">
            {t('port_labels')}
          </Label>
        </div>

        <div className="mx-1 h-5 w-px bg-border" />

        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={highlightVlanRef || 'all'} onValueChange={(v) => setHighlightVlanRef(v === 'all' ? null : v)}>
            <SelectTrigger className="h-7 w-[180px] text-xs" data-testid="highlight-vlan-select">
              <SelectValue placeholder={t('highlight_vlan')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('highlight_all')}</SelectItem>
              {vlans.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: v.color }} />
                    VLAN {v.vlanId} · {v.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mx-1 h-5 w-px bg-border" />

        <ElementZoomControl page={page} />
      </div>
      <div className="netdraw-canvas relative min-h-0 w-full flex-1 bg-[#e9e9e4]" ref={wrapperRef} data-testid="diagram-canvas">
        <SheetBackground />
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onConnect={onConnect}
          onNodesDelete={onNodesDelete}
          onEdgesDelete={onEdgesDelete}
          onNodeClick={(_, node) => selectNode(node.id)}
          onEdgeClick={(_, edge) => selectEdge(edge.id)}
          onPaneClick={() => selectNode(null)}
          onDrop={onDrop}
          onDragOver={onDragOver}
          defaultEdgeOptions={{ type: 'connection' }}
          connectionMode="loose"
          snapToGrid={snapEnabled}
          snapGrid={[16, 16]}
          minZoom={0.1}
          maxZoom={3}
          onInit={() => requestAnimationFrame(fitSheet)}
          proOptions={{ hideAttribution: true }}
          deleteKeyCode={['Backspace', 'Delete']}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
};
